import { RegistrationBodyDto } from "@/api/types/main/schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useUserStore } from "@/store/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Имя должно содержать более 2 символов." })
    .max(15, { message: "Имя не может превышать 15 символов" }),
  surname: z
    .string()
    .min(2, { message: "Фамилия должна содержать более 2 символов." })
    .max(20, { message: "Фамилия не может превышать 20 символов" }),
  email: z
    .string()
    .email({ message: "Введите корректный email." })
    .min(20, { message: "email не должен быть менее 20 символов." })
    .max(40, { message: "email не может превышать 40 символов" })
    .refine((email) => email.endsWith("@champ-commerce.ru"), {
      message: "Email должен заканчиваться на @champ-commerce.ru",
    }),
  password: z
    .string()
    .min(6, { message: "Пароль должен содержать более 6 символов." })
    .max(32, { message: "Пароль не может превышать 32 символа" }),
  gender: z.enum(["MALE", "FEMALE"], { message: "Выберите пол." }),
  isActivate: z.boolean(),
});

export function RegistrationPage() {
  const registration = useUserStore((state) => state.registration);
  const isAuth = useUserStore((state) => state.isAuth);

  const location = useLocation();
  const navigate = useNavigate();
  const fromPage = location.state?.pathname;
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuth) {
      navigate(fromPage || "/");
    }
  }, [isAuth, fromPage, navigate]);
  const { mutate: register, isPending } = useMutation({
    mutationFn: async (variables: RegistrationBodyDto) => await registration(variables),
    onError: (error: AxiosError<{ type: string; error?: string }>) => {
      if (error.response?.data?.type === "email-exist") {
        form.setError("email", {
          type: "manual",
          message: "Этот email уже зарегистрирован.",
        });
      } else if (error.response?.data?.type === "bad-password") {
        form.setError("password", {
          type: "manual",
          message: error.response.data.error,
        });
      } else {
        setFormError("Произошла ошибка при регистрации. Попробуйте снова.");
      }
    },
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      surname: "",
      password: "",
      email: "",
      gender: "MALE",
      isActivate: false,
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setFormError(null);
    form.clearErrors();
    await register(values);
  }

  return (
    <div className="flex h-screen justify-center items-center">
      <Card className="mx-auto max-w-sm px-2">
        <CardHeader>
          <CardTitle className="text-xl">Регистрация</CardTitle>
          <CardDescription>Заполните необходимые поля для регистрации на платформе</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {formError && <div className="text-red-500 text-center mb-4">{formError}</div>}
              <div className="grid gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="email">Email</FormLabel>
                      <FormControl>
                        <Input id="email" placeholder="email@champ-commerce.ru" {...field} />
                      </FormControl>
                      <FormDescription>Введите вашу корпоративную почту</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="password">Пароль</FormLabel>
                      <FormControl>
                        <Input id="password" type="password" placeholder="••••••••" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="name">Имя</FormLabel>
                        <FormControl>
                          <Input id="name" placeholder="Максим" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="surname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="surname">Фамилия</FormLabel>
                        <FormControl>
                          <Input id="surname" placeholder="Иванов" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Выберите пол</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex justify-around"
                        >
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="MALE" />
                            </FormControl>
                            <FormLabel className="font-normal">Мужской</FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="FEMALE" />
                            </FormControl>
                            <FormLabel className="font-normal">Женский</FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full">
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Загрузка...
                    </>
                  ) : (
                    "Создать аккаунт"
                  )}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>
                  Войти
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
